import { Link } from "react-router-dom";
import SafeImage from "./SafeImage";
import { Box, RotateCw } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

export default function ProductCard({ product }) {
  const { settings } = useSettings();
  const soldLabel = settings?.archive?.badge_label || "Found a New Home";
  const sold = product.product_status === "sold_out";
  const has360 = (product.media_type === "360" || product.media_type === "mixed") && (product.spin_360_images?.length || 0) > 0;
  const has3D = (product.media_type === "3d" || product.media_type === "mixed") && !!product.model_3d_url;
  const effectivePrice = product.is_sale && product.sale_price ? product.sale_price : product.price;
  const compare = product.is_sale && product.sale_price ? product.price : product.compare_at_price;

  return (
    <Link to={`/product/${product.slug}`} data-testid={`product-card-${product.slug}`}
      className="curio-card group block">
      <div className="relative aspect-[4/5] overflow-hidden bg-[color:var(--forest)] corner-frame">
        <SafeImage src={product.primary_image} alt={product.image_alt || product.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          loading="lazy" />
        {sold && (
          <div className="absolute inset-0 bg-[rgba(9,18,13,0.55)] flex items-center justify-center">
            <div className="sold-stamp">{soldLabel}</div>
          </div>
        )}
        {product.inventory_type === "one_of_one" && !sold && (
          <div className="absolute top-3 left-3 bg-[rgba(9,18,13,0.85)] border border-gold/60 text-gold text-[10px] font-caption px-2 py-1">
            One of One
          </div>
        )}
        {product.is_new_arrival && !sold && (
          <div className="absolute top-3 left-3 bg-brass text-forest text-[10px] font-caption px-2 py-1"
               style={{ marginTop: product.inventory_type === "one_of_one" ? 32 : 0 }}>
            New
          </div>
        )}
        {product.is_sale && !sold && (
          <div className="absolute bottom-3 left-3 bg-oxblood text-parchment text-[10px] font-caption px-2 py-1">
            Sale
          </div>
        )}
        <div className="absolute top-3 right-3 flex gap-1.5">
          {has360 && <span title="360° view" className="bg-[rgba(9,18,13,0.85)] border border-brass/60 text-gold p-1.5" data-testid={`badge-360-${product.slug}`}><RotateCw size={12} /></span>}
          {has3D && <span title="3D view" className="bg-[rgba(9,18,13,0.85)] border border-brass/60 text-gold p-1.5" data-testid={`badge-3d-${product.slug}`}><Box size={12} /></span>}
        </div>
      </div>
      <div className="p-4 sm:p-5 text-center">
        {product.collection && (
          <div className="font-caption text-[10px] text-brass/80 mb-1.5">{product.collection.replace(/-/g, ' ')}</div>
        )}
        <h3 className="font-display text-lg sm:text-xl text-cream leading-tight mb-2">{product.title}</h3>
        <div className="font-serif-italic text-gold text-base">
          {sold ? <span className="text-oxblood">{soldLabel}</span> : (
            <>
              ${effectivePrice.toFixed(2)}
              {compare && compare > effectivePrice && (
                <span className="ml-2 text-parchment/50 line-through text-sm">${compare.toFixed(2)}</span>
              )}
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
